import { useEffect, useId, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { ImagePlus, X } from "lucide-react";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import { validateFileWithPreset } from "@/lib/fileValidation";
import {
  Modal,
  ModalFooter,
  Button,
  ConfirmModal,
  Input,
  Label,
  Select,
  Switch,
} from "@/components/ui";
import {
  MediaUpload,
  CatalogTranslatedField,
  CatalogTranslatePanel,
} from "@/components/common";
import { FileDropzone } from "./FileDropzone";
import { MENU_CATEGORIES, menuCategoryDisplayLabel } from "@/constants";
import { toastApiError } from "@/lib/errorMessages";
import { formatMoneyInput } from "@/lib/money";
import {
  findAutoTranslateSourceLanguage,
  resolveCatalogTranslation,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import { resolveLocale, type SupportedLocale } from "@/lib/locales";
import {
  applyAutoTranslateResult,
  useCatalogAutoTranslate,
} from "@/hooks/useCatalogAutoTranslate";
import type {
  ActivityItem,
  ActivityItemUpdate,
  ServiceCategory,
} from "@/types";
import type { ActivityItemCreateWithImage } from "@/services/activityItemService";
import type { EntityType } from "@/services/storageService";
import {
  buildProductItemBody,
  type ProductItemFormError,
} from "./productItemForm";

export type ProductItem = ActivityItem;

export type ProductItemCreate = ActivityItemCreateWithImage;
export type ProductItemUpdate = ActivityItemUpdate;

export interface ProductItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityId: string;
  editingItem: ProductItem | null;
  /** Item type - determines whether to show category field */
  itemType: "menu" | "service";
  /** Entity type for storage */
  entityType: EntityType;
  /** Query key for the items list */
  queryKey: string;
  /** Service functions */
  createFn: (
    activityId: string,
    data: ProductItemCreate,
  ) => Promise<ProductItem>;
  updateFn: (
    activityId: string,
    itemId: string,
    data: ProductItemUpdate,
  ) => Promise<ProductItem>;
  serviceCategories?: ServiceCategory[];
  /** Platform commission rate (0–1) applied to new catalog lines. */
  activityCommissionRate: number;
}

export function ProductItemModal({
  isOpen,
  onClose,
  activityId,
  editingItem,
  itemType,
  entityType,
  queryKey,
  createFn,
  updateFn,
  serviceCategories = [],
  activityCommissionRate,
}: ProductItemModalProps) {
  const { t, i18n } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const formId = useId();
  const { t: tActivities } = useTranslation("activities");
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!editingItem;
  const isMenu = itemType === "menu";

  // Form state
  const [categoryValue, setCategoryValue] = useState("");
  const [nameTranslations, setNameTranslations] =
    useState<TranslationFormValues>({});
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationFormValues>({});
  const [copyLanguage, setCopyLanguage] = useState<SupportedLocale>(() =>
    resolveLocale(i18n.language),
  );
  const [price, setPrice] = useState("");
  const [showKioskDiscount, setShowKioskDiscount] = useState(false);
  const [undiscountedPrice, setUndiscountedPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [error, setError] = useState<ProductItemFormError | "">("");
  // Image for new items (before they have an ID)
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const currentImage = editingItem?.image_url ?? undefined;

  const preferredSourceLanguage = resolveLocale(i18n.language);
  const autoTranslateSourceLanguage =
    findAutoTranslateSourceLanguage(
      nameTranslations,
      preferredSourceLanguage,
    ) ?? preferredSourceLanguage;

  const resetForm = () => {
    setCategoryValue("");
    setNameTranslations({});
    setDescriptionTranslations({});
    setPrice("");
    setShowKioskDiscount(false);
    setUndiscountedPrice("");
    setDiscountedPrice("");
    setError("");
    setSelectedImage(null);
    setImagePreview((prev) => {
      if (prev?.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
  };

  // Initialize form when editing
  useEffect(() => {
    if (editingItem) {
      if (isMenu && editingItem.menu_category) {
        setCategoryValue(editingItem.menu_category);
      } else {
        setCategoryValue(editingItem.service_category_id || "");
      }
      setNameTranslations({ ...editingItem.name_translations });
      setDescriptionTranslations(
        editingItem.description_translations
          ? { ...editingItem.description_translations }
          : {},
      );
      if (editingItem.undiscounted_price != null) {
        setShowKioskDiscount(true);
        setUndiscountedPrice(formatMoneyInput(editingItem.undiscounted_price));
        setDiscountedPrice(formatMoneyInput(editingItem.price));
      } else {
        setShowKioskDiscount(false);
        setUndiscountedPrice("");
        setDiscountedPrice("");
        setPrice(formatMoneyInput(editingItem.price));
      }
    } else {
      resetForm();
    }
  }, [editingItem, isOpen, isMenu]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectedImage = (file: File) => {
    const result = validateFileWithPreset(file, "catalogImage");
    if (!result.ok) {
      toast.error(t("productItem.imageTooLarge"));
      return;
    }

    setSelectedImage(file);
    setImagePreview((prev) => {
      if (prev?.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ProductItemCreate) => createFn(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey, activityId] });
      toast.success(
        isMenu
          ? t("productItem.menuItemCreated")
          : t("productItem.serviceCreated"),
      );
      handleClose();
    },
    onError: (err) => {
      toastApiError(err, isMenu ? "productCreateMenu" : "productCreateService");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ProductItemUpdate) =>
      updateFn(activityId, editingItem!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey, activityId] });
      toast.success(
        isMenu
          ? t("productItem.menuItemUpdated")
          : t("productItem.serviceUpdated"),
      );
      handleClose();
    },
    onError: (err) => {
      toastApiError(err, isMenu ? "productUpdateMenu" : "productUpdateService");
    },
  });

  const handleImageChange = async () => {
    // The backend syncs the item's image_url on storage upload/delete; refresh the list.
    await queryClient.invalidateQueries({
      queryKey: [queryKey, activityId],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Portaled modals still bubble submit through the React tree; do not submit a parent form.
    e.stopPropagation();
    setError("");

    const result = buildProductItemBody({
      itemType,
      categoryValue,
      nameTranslations,
      descriptionTranslations,
      price,
      showKioskDiscount,
      undiscountedPrice,
      discountedPrice,
      savedUndiscountedPrice: editingItem?.undiscounted_price ?? null,
    });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const baseData = result.body;

    if (isEditing) {
      updateMutation.mutate(baseData);
    } else {
      const data = {
        ...baseData,
        commission_rate: activityCommissionRate,
        image: selectedImage || undefined,
      };
      createMutation.mutate(data);
    }
  };

  const autoTranslate = useCatalogAutoTranslate({
    activityId,
    sourceLanguage: autoTranslateSourceLanguage,
    nameTranslations,
    descriptionTranslations,
    requireDescription: false,
    onApplied: (result) => {
      const applied = applyAutoTranslateResult(
        nameTranslations,
        descriptionTranslations,
        result,
        {
          mergeDescriptions: Boolean(result.description_translations),
        },
      );
      setNameTranslations(applied.nameTranslations);
      setDescriptionTranslations(applied.descriptionTranslations);
      setError("");
    },
    onMissingName: () => setError("name"),
  });

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    autoTranslate.isPending;

  const categoryOptions = [
    { value: "", label: t("productItem.noCategory") },
    ...(isMenu
      ? MENU_CATEGORIES.map((cat) => ({
          value: cat.value,
          label: menuCategoryDisplayLabel(cat.value),
        }))
      : []),
    ...serviceCategories.map((category) => ({
      value: category.id,
      label: resolveCatalogTranslation(
        category.name_translations,
        i18n.language,
      ),
    })),
  ];

  const title = isEditing
    ? isMenu
      ? t("productItem.titleEditMenu")
      : t("productItem.titleEditService")
    : isMenu
      ? t("productItem.titleAddMenu")
      : t("productItem.titleAddService");

  return (
    <>
      <Modal
        closeLabel={tCommon("close")}
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        size="lg"
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant={isMenu ? "accent" : "primary"}
              isLoading={isPending}
            >
              {isEditing
                ? t("productItem.saveChanges")
                : isMenu
                  ? t("productItem.addItem")
                  : t("productItem.addService")}
            </Button>
          </ModalFooter>
        }
      >
        <form
          id={formId}
          onSubmit={handleSubmit}
        >
          <div className="space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row">
              {isEditing ? (
                <MediaUpload
                  value={currentImage}
                  onChange={handleImageChange}
                  entityType={entityType}
                  entityId={editingItem.id}
                  mediaType="image"
                  label={t("productItem.photo")}
                  hint={t("productItem.photoHint")}
                  size="md"
                />
              ) : (
                <div className="flex flex-col gap-1">
                  <Label
                    as="span"
                    spacing="none"
                  >
                    {t("productItem.photo")}
                  </Label>
                  {imagePreview ? (
                    <div className="relative h-28 w-28">
                      <img
                        src={imagePreview}
                        alt={t("productItem.previewAlt")}
                        className="border-surface-200 h-full w-full rounded-lg border object-cover"
                      />
                      <button
                        type="button"
                        onClick={clearSelectedImage}
                        aria-label={tCommon("delete")}
                        title={tCommon("delete")}
                        className="hover:bg-error-fill absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-md bg-black/55 text-white shadow-sm backdrop-blur-sm transition-colors"
                      >
                        <X
                          className="h-3.5 w-3.5"
                          aria-hidden
                        />
                      </button>
                    </div>
                  ) : (
                    <FileDropzone
                      id="product-item-create-photo"
                      accept={FILE_ACCEPT.catalogImage}
                      inputRef={fileInputRef}
                      className="text-surface-400 hover:text-primary-500 h-28 w-28 gap-1 px-2 py-2"
                      icon={ImagePlus}
                      iconClassName="h-6 w-6"
                      title={t("productItem.addPhoto")}
                      titleClassName="text-xs font-normal text-surface-400"
                      onFilesSelected={(files) => {
                        const file = files[0];
                        if (file) {
                          handleSelectedImage(file);
                        }
                      }}
                    />
                  )}
                  <span className="text-surface-400 text-xs">
                    {tCommon("optional")}
                  </span>
                </div>
              )}

              <div className="flex-1 space-y-4">
                <Select
                  label={t("productItem.category")}
                  value={categoryValue}
                  onChange={(e) => setCategoryValue(e.target.value)}
                  options={categoryOptions}
                />

                <Switch
                  checked={showKioskDiscount}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setShowKioskDiscount(enabled);
                    if (enabled && !undiscountedPrice && price) {
                      setUndiscountedPrice(price);
                      setDiscountedPrice("");
                    }
                    if (!enabled) {
                      if (discountedPrice) {
                        setPrice(discountedPrice);
                      }
                      setUndiscountedPrice("");
                      setDiscountedPrice("");
                    }
                  }}
                  label={t("productItem.showKioskDiscountLabel")}
                  description={t("productItem.showKioskDiscountHint")}
                />

                {showKioskDiscount ? (
                  <>
                    <Input
                      label={t("productItem.undiscountedPriceEuro")}
                      inputMode="decimal"
                      value={undiscountedPrice}
                      onChange={(e) => setUndiscountedPrice(e.target.value)}
                      required
                      error={
                        error === "undiscountedPrice"
                          ? t("productItem.validUndiscountedPriceRequired")
                          : undefined
                      }
                    />
                    <Input
                      label={t("productItem.discountedPriceEuro")}
                      inputMode="decimal"
                      value={discountedPrice}
                      onChange={(e) => setDiscountedPrice(e.target.value)}
                      required
                      error={
                        error === "discountedPrice"
                          ? t("productItem.validDiscountedPriceRequired")
                          : error === "discountedMustBeLower"
                            ? t("productItem.discountedMustBeLower")
                            : undefined
                      }
                    />
                  </>
                ) : (
                  <Input
                    label={t("productItem.priceEuro")}
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder={
                      isMenu
                        ? t("productItem.placeholderPriceMenu")
                        : t("productItem.placeholderPriceService")
                    }
                    required
                    error={
                      error === "price"
                        ? t("productItem.validPriceRequired")
                        : undefined
                    }
                  />
                )}
              </div>
            </div>

            <CatalogTranslatePanel
              hint={t("productItem.autoTranslateHint")}
              buttonLabel={t("productItem.autoTranslateGenerate")}
              onTranslate={autoTranslate.run}
              isLoading={autoTranslate.isPending}
              disabled={isPending}
            />

            <CatalogTranslatedField
              values={nameTranslations}
              onChange={setNameTranslations}
              label={t("productItem.name")}
              fieldAriaLabel={(language) =>
                tActivities("detailForm.nameLanguageCardAria", { language })
              }
              placeholder={
                isMenu
                  ? t("productItem.placeholderNameMenu")
                  : t("productItem.placeholderNameService")
              }
              required
              error={
                error === "name"
                  ? t("productItem.allTranslationsRequired")
                  : undefined
              }
              selectedLanguage={copyLanguage}
              onSelectedLanguageChange={setCopyLanguage}
            />

            <CatalogTranslatedField
              values={descriptionTranslations}
              onChange={setDescriptionTranslations}
              label={t("productItem.description")}
              fieldAriaLabel={(language) =>
                tActivities("detailForm.descriptionLanguageCardAria", {
                  language,
                })
              }
              multiline
              requireAllLocales={false}
              selectedLanguage={copyLanguage}
              onSelectedLanguageChange={setCopyLanguage}
              hideLanguageControls
              placeholder={
                isMenu
                  ? t("productItem.placeholderDescMenu")
                  : t("productItem.placeholderDescService")
              }
              hint={
                isMenu
                  ? t("productItem.hintDescMenu")
                  : t("productItem.hintDescService")
              }
              error={
                error === "descriptionPartial"
                  ? t("productItem.descriptionPartial")
                  : undefined
              }
            />
          </div>
        </form>
      </Modal>
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={autoTranslate.confirmOpen}
        onClose={autoTranslate.onConfirmClose}
        onConfirm={autoTranslate.onConfirm}
        title={autoTranslate.confirmTitle}
        message={autoTranslate.confirmMessage}
        isLoading={autoTranslate.isPending}
      />
    </>
  );
}
